import { Tooltip } from '@/components/ui/tooltip';
import { IconButton } from '@chakra-ui/react';
import type { ComponentType, SVGProps } from 'react';

/**
 * The look of a row action: a 32px outlined square. Shared with the row menus
 * (a "more actions" trigger), so every action column reads the same.
 */
export const rowActionIconButtonStyle = {
  // `xs` is the 32px square; its recipe icon size (16px) is overridden below.
  size: 'xs',
  variant: 'outline',
  rounded: 'md',
  borderColor: 'hairline.field',
  bg: 'white',
  color: 'navInactive',
  _icon: { boxSize: '17px' },
  _hover: { bg: 'softCream', borderColor: 'hairline.controlHover', color: 'ink' },
} as const;

type RowActionIconButtonProps = {
  icon: ComponentType<SVGProps<SVGSVGElement>>;
  /** Tooltip text and accessible name, always the same words. */
  label: string;
  loading?: boolean;
  'data-testid'?: string;
} & ({ onClick: () => void; href?: never } | { href: string; onClick?: never });

/**
 * One icon button of a table's row actions column: a 32px outlined square
 * behind a tooltip. It swallows the click itself, because the whole row opens
 * its own drawer and an action must never open it too.
 */
export const RowActionIconButton = ({
  icon: Icon,
  label,
  onClick,
  href,
  loading,
  'data-testid': testId,
}: RowActionIconButtonProps) => (
  <Tooltip content={label} portalled positioning={{ placement: 'top' }}>
    <IconButton
      asChild={Boolean(href)}
      aria-label={label}
      loading={loading}
      onClick={(event) => {
        event.stopPropagation();
        onClick?.();
      }}
      data-testid={testId}
      {...rowActionIconButtonStyle}
    >
      {href ? (
        <a href={href} target="_blank" rel="noopener noreferrer">
          <Icon />
        </a>
      ) : (
        <Icon />
      )}
    </IconButton>
  </Tooltip>
);
